import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { Controller } from 'react-hook-form'
import { FormControl, InputLabel, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import SimpleDialog from 'components/ui/SimpleDialog'
import { selectCountries, selectCountriesWithClubs } from 'store/countries/slice'
import { getCountries, getCountriesWithClubs } from 'store/countries/thunks'
import CustomTooltip from 'components/ui/CustomTooltip'
import { FormattedMessageWrapper } from 'components/ui/FormattedMessageWrapper'

interface Props {
  name: string
  label?: string
  labelPosition?: 'top' | 'left'
  control: any
  initialValue: SelectOption[] | SelectOption | null
  onlyCountriesWithClubs?: boolean
  required?: boolean
  multiple?: boolean
  courseEditor?: boolean
  handleCountryChange?: (arg: Country[]) => void
  disabledCountries?: number[]
}

export default function CountrySelect({
  name,
  control,
  initialValue = null,
  onlyCountriesWithClubs,
  label,
  labelPosition = 'top',
  required = false,
  multiple = false,
  courseEditor = false,
  handleCountryChange,
  disabledCountries,
}: Props): JSX.Element {
  const intl = useIntl()
  const [values, setValues] = useState<SelectOption[] | SelectOption | null>(initialValue)
  const [options, setOptions] = useState<SelectOption[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const { countries, status } = useSelector(onlyCountriesWithClubs ? selectCountriesWithClubs : selectCountries)
  const dispatch = useDispatch()

  useEffect(() => {
    if (['idle', 'failed'].includes(status)) {
      if (onlyCountriesWithClubs) {
        dispatch(getCountriesWithClubs())
      } else {
        dispatch(getCountries())
      }
    }
  }, [dispatch, onlyCountriesWithClubs, status])

  useEffect(() => {
    if (countries) {
      const countryOptions = countries.map((country: Country) => {
        return { key: country.id, label: country.name }
      })

      if (courseEditor) {
        setOptions([{ key: 'all', label: intl.formatMessage({ id: 'strings.allCountries' }) }, ...countryOptions])
      } else {
        setOptions(countryOptions)
      }
    }
  }, [countries, courseEditor, intl])

  useEffect(() => {
    const isArray = Array.isArray(initialValue)
    const notEmptyMultipleInitialValue = multiple && isArray && Array.isArray(values) && values.length === 0
    const notEmptySingleInitialValue = !multiple && initialValue && !values
    const emptyInitialValue = (Array.isArray(initialValue) && initialValue.length === 0) || initialValue === null
    if (notEmptyMultipleInitialValue || notEmptySingleInitialValue || emptyInitialValue) {
      setValues(initialValue)
    }
  }, [initialValue, values, multiple])

  const prevInitialValue = useRef(initialValue)

  // This re-renders the component if trying to set user's country as initial value
  useEffect(() => {
    if (initialValue !== prevInitialValue.current) {
      setValues(initialValue)
      prevInitialValue.current = initialValue
    }
  }, [initialValue])

  const handleChange = (newValues: SelectOption[] | SelectOption | null) => {
    if (handleCountryChange) {
      let countryList: Country[] = []
      if (newValues) {
        countryList = formatOptionsToCountries(newValues)
      }
      handleCountryChange(countryList)
    }
    setValues(newValues)
    return newValues
  }

  const formatOptionsToCountries = (newValues: SelectOption[] | SelectOption): Country[] => {
    const options = Array.isArray(newValues) ? newValues : [newValues]
    const countryIds = options.map((c: SelectOption) => c.key).flat()

    return countries.filter((country: Country) => countryIds.includes(country.id))
  }

  const getPlaceholderText = () => {
    if (required) {
      return intl.formatMessage({ id: 'strings.required' })
    }
    return intl.formatMessage({ id: 'strings.search' })
  }

  const getValues = () => {
    return countries && values ? values : null
  }

  const ContainerVariant = labelPosition === 'left' ? LabelInputRowContainer : FormControl
  const Container = courseEditor ? CourseEditorLabelInputContainer : ContainerVariant

  return (
    <>
      <Container margin="normal" fullWidth required={required}>
        {!courseEditor && <FieldLabel>{label}</FieldLabel>}
        <Controller
          control={control}
          name={name}
          defaultValue={getValues()}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              loading={status === 'loading'}
              disableListWrap
              onChange={(event, newValues) => {
                const key = Array.isArray(newValues) ? newValues[0]?.key : newValues?.key

                if (newValues && disabledCountries?.includes(key)) {
                  event.preventDefault()
                  setModalOpen(true)
                } else {
                  onChange(handleChange(newValues))
                }
              }}
              options={options}
              getOptionLabel={(option) => option.label || ''}
              autoHighlight
              renderOption={(props, option) => {
                const isDisabled = disabledCountries?.includes(option.key)
                return isDisabled ? (
                  <CustomTooltip
                    position="left"
                    title={intl.formatMessage({ id: 'courseEditor.cannotAddCourseInCountry' })}
                  >
                    <Option {...props} onClick={() => setModalOpen(true)} style={{ opacity: 0.7 }}>
                      {option.label}
                    </Option>
                  </CustomTooltip>
                ) : (
                  <Option {...props}>{option.label}</Option>
                )
              }}
              value={value}
              multiple={multiple}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={getPlaceholderText()}
                  required={required}
                  label={courseEditor ? label : null}
                  InputLabelProps={{ style: { color: courseEditor ? '#373D40' : '#1AA85D', fontSize: 14 } }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.key === value.key}
            />
          )}
        />
        <SimpleDialog
          buttonText={intl.formatMessage({ id: 'gpsEditorTutorial.okButton' })}
          onClose={() => setModalOpen(false)}
          open={modalOpen}
          text={
            <FormattedMessageWrapper
              id="courseEditor.editingNotAvailable"
              values={{ b: (chunks) => <b>{chunks}</b> }}
            />
          }
          title={intl.formatMessage({ id: 'strings.limitedAccess' })}
        />
      </Container>
    </>
  )
}

const LabelInputRowContainer = styled(FormControl)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  margin: 5px 0 30px;

  & > label {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    position: relative;
    height: 40px;
    width: 100px;
    margin: 0 10px 0 0;
  }
  & > div {
    height: 40px;
    flex: 1;
  }
`

const CourseEditorLabelInputContainer = styled(FormControl)`
  .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] {
    height: 53px;
  }
`

const FieldLabel = styled(InputLabel)`
  padding: 0 10px;
  margin-top: -20px;
  transition: none;
  transform: none;
`

const Option = styled.span`
  display: block;
`
